import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import type { SelectOptionType } from '../../Controls/Select/types';
import type {
  DataSource,
  DependsOnEntry,
  FilterBarController,
  FilterFieldPhase,
  FilterItemConfig,
  FilterValues,
  UseFilterBarConfig,
} from '../types';
import { validateItems } from '../state/invariants';
import {
  buildGraph,
  getDescendants,
  getDirectDependents,
  type DependencyGraph,
} from '../state/dependency-graph';
import { FetchRegistry } from '../state/fetch-registry';
import { OptionsCache } from '../state/options-cache';
import {
  canonicalEmpty,
  currentValues,
  initialState,
  reducer,
  type State,
} from '../state/reducer';
import {
  emitIfReady,
  shallowEqualValue,
  shallowEqualValues,
} from '../state/init-sequencer';

type OrchestratorOptions = {
  source: 'cascade' | 'imperative' | 'user';
  trigger?: string;
  /** When true, skip running cascade — used by setMany({ cascade: false }). */
  skipCascade?: boolean;
};

export function useFilterBar<T extends FilterValues = FilterValues>(
  config: UseFilterBarConfig<T>,
): FilterBarController<T> {
  const {
    items,
    initialValues,
    values: controlledValues,
    persistence,
    autoSave = true,
    initialVisibleFields,
    onInit,
    onChange,
    onFieldChange,
  } = config;

  // Snapshot items as ref so reducer/orchestrator sees current values without
  // depending on closures.
  const itemsRef = useRef<FilterItemConfig[]>(items);
  itemsRef.current = items;

  // Build graph once per unique items shape — cheap to rebuild on items change.
  const graph: DependencyGraph = useMemo(() => {
    validateItems(items);
    return buildGraph(items);
  }, [items]);

  const [state, reactDispatch] = useReducer(reducer, items, initialState);
  const stateRef = useRef<State>(state);
  stateRef.current = state;

  // Synchronously update stateRef AND schedule React render. The orchestrator
  // reads stateRef.current between awaits and needs to see post-dispatch state
  // immediately, not wait for React's render commit.
  const dispatch = useCallback((action: Parameters<typeof reactDispatch>[0]) => {
    stateRef.current = reducer(stateRef.current, action);
    reactDispatch(action);
  }, []);

  // Reconcile reducer when items prop changes (after first render).
  // Plan: "items array reference stability not required." Consumers may rebuild
  // the items array each render — we only reconcile on a structural change
  // (name/type/dependsOn). A bare reference change with identical structure is a no-op,
  // preventing infinite render loops when items is built inline in JSX.
  const itemsSignatureRef = useRef<string>('');
  const firstRender = useRef(true);
  useEffect(() => {
    const signature = items.map((i) => {
      const deps = (i.dependsOn ?? []).map((d) => `${d.name}~${d.onParentChange ?? 'reset'}`).join(',');
      return `${i.name}|${i.type}|${deps}`;
    }).join('||');
    if (firstRender.current) {
      firstRender.current = false;
      itemsSignatureRef.current = signature;
      return;
    }
    if (signature === itemsSignatureRef.current) return;
    itemsSignatureRef.current = signature;
    validateItems(items);
    const previouslyKnown = new Set(stateRef.current.itemsOrder);
    dispatch({ type: 'RECONCILE_ITEMS', items });
    queueReconcileInit(items, previouslyKnown);
  }, [items]);

  const registry = useRef(new FetchRegistry());
  const cache = useRef(new OptionsCache());
  const initializedRef = useRef(false);
  const initStartedRef = useRef(false);
  const pendingTxnRef = useRef(0);

  // Persistence orchestration
  const lastPersistedRef = useRef<FilterValues | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // forceTickForDirty bumps each save/load/clear so the persistence-controller memo
  // re-evaluates isDirty against the (possibly mutated) lastPersistedRef.
  const [persistenceTick, setPersistenceTick] = useState(0);

  const callbacksRef = useRef({ onInit, onChange, onFieldChange });
  callbacksRef.current = { onInit, onChange, onFieldChange };

  // ---------- Helpers ----------

  const getItem = (name: string): FilterItemConfig | undefined => stateRef.current.itemsByName[name];

  const getParentValues = useCallback(
    (name: string): FilterValues => {
      const parents = graph.parentsOf.get(name) ?? new Set<string>();
      const cur = currentValues(stateRef.current);
      const out: FilterValues = {};
      for (const p of parents) out[p] = cur[p];
      return out;
    },
    [graph],
  );

  const valueIsValidInOptions = (item: FilterItemConfig, value: unknown, options: SelectOptionType[]): boolean => {
    if (item.type === 'SELECT') {
      if (value === null || value === undefined) return true;
      return flattenOptionValues(options).has(value as string | number);
    }
    if (item.type === 'SELECT_MULTI') {
      const arr = (value as Array<string | number> | undefined) ?? [];
      if (arr.length === 0) return true;
      const set = flattenOptionValues(options);
      return arr.every((v) => set.has(v));
    }
    return true;
  };

  /** Open a transaction if not already open. */
  const ensureTxnOpen = (source: 'cascade' | 'imperative' | 'user', trigger?: string) => {
    if (!stateRef.current.transactionOpen) {
      dispatch({ type: 'CASCADE_OPEN', source, trigger });
    }
  };

  /** Increment txn pending — orchestrator unit of async work. */
  const txnIncrement = () => {
    pendingTxnRef.current += 1;
  };

  const txnDecrement = () => {
    pendingTxnRef.current -= 1;
    if (pendingTxnRef.current <= 0) {
      pendingTxnRef.current = 0;
      if (stateRef.current.transactionOpen) {
        dispatch({ type: 'CASCADE_CLOSE' });
      }
    }
  };

  /**
   * Resolve a field's "baseline" value, uniform across all variants:
   *   1. static `defaultValue` (if set) — always wins.
   *   2. else `getDefaultValue` (if set) — runs against the field's options.
   *   3. else canonical empty.
   */
  const resolveDefault = async (item: FilterItemConfig, options: SelectOptionType[]): Promise<unknown> => {
    if (item.defaultValue !== undefined) return item.defaultValue;
    if (typeof item.getDefaultValue === 'function') {
      try {
        const cur = currentValues(stateRef.current);
        const v = await item.getDefaultValue(options, cur);
        return v;
      } catch (err) {
        console.warn(`[FilterBar] getDefaultValue for "${item.name}" threw:`, err);
        return null;
      }
    }
    return canonicalEmpty(item);
  };

  /**
   * Load options for a single field (static OR async), respecting cacheKey short-circuit.
   * Returns the resolved options or null if discarded due to staleness/abort.
   */
  const loadOptionsForField = async (name: string, opts: { useCache?: boolean } = {}): Promise<SelectOptionType[] | null> => {
    const item = getItem(name);
    if (!item) return null;

    // SELECT, SELECT_MULTI, SEGMENT_TOGGLE all carry a `dataSource`; other variants
    // (TEXT_SEARCH, DATE_RANGE, NUMERIC_RANGE) don't have options.
    const ds: DataSource | undefined = (item as { dataSource?: DataSource }).dataSource;
    if (!ds) {
      dispatch({ type: 'FIELD_PHASE', name, phase: 'READY' });
      return [];
    }

    const parents = Array.from(graph.parentsOf.get(name) ?? []);

    if (ds.type === 'static') {
      const { requestId } = registry.current.start(name);
      dispatch({ type: 'FIELD_OPTIONS_LOADING', name, requestId });
      dispatch({ type: 'FIELD_OPTIONS_RESOLVED', name, requestId, options: ds.options });
      return ds.options;
    }

    // async
    const parentValues = getParentValues(name);
    const cacheKey = ds.cacheKey ? ds.cacheKey(parentValues) : OptionsCache.defaultCacheKey(parents, parentValues);

    if (opts.useCache !== false) {
      const cached = cache.current.get(name, cacheKey);
      if (cached) {
        const { requestId } = registry.current.start(name);
        dispatch({ type: 'FIELD_OPTIONS_LOADING', name, requestId });
        dispatch({ type: 'FIELD_OPTIONS_RESOLVED', name, requestId, options: cached });
        return cached;
      }
    }

    const { signal, requestId } = registry.current.start(name);
    dispatch({ type: 'FIELD_OPTIONS_LOADING', name, requestId });
    try {
      const options = await ds.load(parentValues, signal);
      if (signal.aborted) return null;
      if (!registry.current.isCurrent(name, requestId)) return null;
      cache.current.set(name, cacheKey, options);
      dispatch({ type: 'FIELD_OPTIONS_RESOLVED', name, requestId, options });
      return options;
    } catch (err) {
      if (signal.aborted) return null;
      if (!registry.current.isCurrent(name, requestId)) return null;
      const message = err instanceof Error ? err.message : String(err);
      dispatch({ type: 'FIELD_OPTIONS_FAILED', name, requestId, error: message });
      return null;
    }
  };

  /**
   * Stale-value handling — called after options resolve.
   * Returns the new value (may be same as current).
   */
  const handleStaleValueAndDefault = async (
    name: string,
    options: SelectOptionType[],
    parentChangePolicy?: DependsOnEntry['onParentChange'],
  ): Promise<void> => {
    const item = getItem(name);
    if (!item) return;
    const field = stateRef.current.fields[name];
    if (!field) return;

    const policy = parentChangePolicy ?? 'reset';
    const currentValue = field.value;
    const isValid = valueIsValidInOptions(item, currentValue, options);

    // Apply onParentChange policy if this is a cascade
    if (parentChangePolicy) {
      if (policy === 'reset' || policy === 'reset-to-default') {
        // Clear value
        dispatch({
          type: 'FIELD_SET_VALUE',
          name,
          value: canonicalEmpty(item),
          userTouched: false,
        });
        if (policy === 'reset') {
          // Don't re-run getDefaultValue
          dispatch({ type: 'FIELD_PHASE', name, phase: 'READY' });
          return;
        }
      } else if (policy === 'preserve-if-valid') {
        if (!isValid) {
          dispatch({
            type: 'FIELD_SET_VALUE',
            name,
            value: canonicalEmpty(item),
            userTouched: false,
          });
        } else {
          dispatch({ type: 'FIELD_PHASE', name, phase: 'READY' });
          return;
        }
      }
    } else if (!isValid) {
      // Init flow or non-cascade reload — apply stale handling logic per plan.
      if (!field.userTouched) {
        // Clear, then re-run default
        dispatch({
          type: 'FIELD_SET_VALUE',
          name,
          value: canonicalEmpty(item),
          userTouched: false,
        });
      } else if (item.clearable !== false) {
        dispatch({
          type: 'FIELD_SET_VALUE',
          name,
          value: canonicalEmpty(item),
          userTouched: false,
        });
      } else {
        // userTouched && !clearable
        const fallback = await resolveDefault(item, options);
        if (fallback === null || fallback === undefined) {
          dispatch({
            type: 'FIELD_OPTIONS_FAILED',
            name,
            requestId: field.requestId ?? '',
            error: `Stale value with no valid default for non-clearable field "${name}".`,
          });
          return;
        }
        dispatch({ type: 'FIELD_SET_VALUE', name, value: fallback });
        dispatch({ type: 'FIELD_PHASE', name, phase: 'READY' });
        return;
      }
    }

    // Re-read field state after possible value clear
    const next = stateRef.current.fields[name];
    const needsDefault = next.value === undefined
      || next.value === null
      || (Array.isArray(next.value) && next.value.length === 0)
      || next.value === '';

    if (needsDefault && !next.userTouched) {
      dispatch({ type: 'FIELD_PHASE', name, phase: 'LOADING_DEFAULT' });
      const defaultValue = await resolveDefault(item, options);
      if (defaultValue !== null && defaultValue !== undefined) {
        dispatch({ type: 'FIELD_SET_VALUE', name, value: defaultValue });
      } else if (item.clearable === false) {
        dispatch({
          type: 'FIELD_ERROR',
          name,
          error: `No valid default available for non-clearable field "${name}".`,
        });
        return;
      }
    }
    dispatch({ type: 'FIELD_PHASE', name, phase: 'READY' });
  };

  /**
   * Initialize a single field — load options, then handle defaults.
   * Used during init flow.
   */
  const initializeField = async (name: string): Promise<void> => {
    txnIncrement();
    try {
      const item = getItem(name);
      if (!item) return;
      const options = await loadOptionsForField(name);
      if (!options) return;
      await handleStaleValueAndDefault(name, options);
    } finally {
      txnDecrement();
    }
  };

  /**
   * Cascade reload — for a field that just had a parent change.
   * Applies onParentChange policy, reloads options, runs handlers, then descends.
   */
  const cascadeField = async (name: string, policy: DependsOnEntry['onParentChange']): Promise<void> => {
    txnIncrement();
    try {
      const item = getItem(name);
      if (!item) return;
      const valueBefore = stateRef.current.fields[name]?.value;

      const options = await loadOptionsForField(name, { useCache: true });
      if (!options) return;

      await handleStaleValueAndDefault(name, options, policy);

      // If value changed, cascade to direct dependents.
      const valueAfter = stateRef.current.fields[name]?.value;
      if (!shallowEqualValue(valueBefore, valueAfter)) {
        const dependents = getDirectDependents(graph, name);
        await Promise.all(
          dependents.map((dep) => {
            const item2 = getItem(dep);
            const policyForDep = item2?.dependsOn?.find((d) => d.name === name)?.onParentChange ?? 'reset';
            return cascadeField(dep, policyForDep);
          }),
        );
      }
    } finally {
      txnDecrement();
    }
  };

  /**
   * Trigger a cascade transaction starting at `name`. `name` is assumed to have
   * been set already; this kicks off the dependents.
   */
  const runCascadeFrom = async (
    name: string,
    options: OrchestratorOptions,
  ): Promise<void> => {
    ensureTxnOpen(options.source, options.trigger ?? name);
    if (options.skipCascade) return;
    const dependents = getDirectDependents(graph, name);
    if (dependents.length === 0) return;

    txnIncrement();
    try {
      await Promise.all(
        dependents.map((dep) => {
          const item = getItem(dep);
          const policy = item?.dependsOn?.find((d) => d.name === name)?.onParentChange ?? 'reset';
          return cascadeField(dep, policy);
        }),
      );
    } finally {
      txnDecrement();
    }
  };

  /** Items reconciliation: spawn init for newly-added fields. */
  const queueReconcileInit = (newItems: FilterItemConfig[], previouslyKnown: Set<string>) => {
    const newFields = newItems.filter((i) => !previouslyKnown.has(i.name));
    if (newFields.length === 0) return;
    txnIncrement();
    ensureTxnOpen('imperative', undefined);
    Promise.all(newFields.map((i) => initializeField(i.name)))
      .catch(() => {})
      .finally(txnDecrement);
  };

  // ---------- Init effect ----------

  useEffect(() => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;

    let cancelled = false;
    const runInit = async () => {
      // Plan Section "Initial value priority":
      //   1) Controlled `values` prop (always wins).
      //   2) Adapter `load()` result (clearable:false fields skip this).
      //   3) `initialValues` / `defaultValues` prop.
      //   4) `FilterItemBase.defaultValue` per field.
      //   5) `getDefaultValue` per field (runs after options load if still unset).
      //   6) null.
      const seededValues: FilterValues = {};
      let persistedLoaded: FilterValues | null = null;

      if (persistence) {
        try {
          const loaded = await Promise.resolve(persistence.load());
          if (loaded && !cancelled) {
            persistedLoaded = loaded;
            lastPersistedRef.current = loaded;
          }
        } catch (err) {
          console.warn('[FilterBar] persistence.load failed:', err);
        }
      }

      for (const item of itemsRef.current) {
        const fromControlled = controlledValues
          ? (controlledValues as FilterValues)[item.name]
          : undefined;
        if (fromControlled !== undefined) {
          seededValues[item.name] = fromControlled;
          continue;
        }
        // Persistence — skip for `clearable: false` fields (a stale persisted
        // value would violate "must always have a valid value").
        if (
          persistedLoaded
          && item.clearable !== false
          && persistedLoaded[item.name] !== undefined
        ) {
          seededValues[item.name] = persistedLoaded[item.name];
          continue;
        }
        const fromInitial = initialValues
          ? (initialValues as FilterValues)[item.name]
          : undefined;
        if (fromInitial !== undefined) {
          seededValues[item.name] = fromInitial;
          continue;
        }
        if (item.defaultValue !== undefined) {
          seededValues[item.name] = item.defaultValue;
        } else {
          // Leave at canonical empty → getDefaultValue runs during init pass
          // (if supported & set).
          seededValues[item.name] = canonicalEmpty(item);
        }
      }

      // Dispatch seeded values
      for (const item of itemsRef.current) {
        dispatch({
          type: 'FIELD_SET_VALUE',
          name: item.name,
          value: seededValues[item.name],
          userTouched: false,
        });
      }

      // Set initial visibility
      const visible = computeInitialVisibility(itemsRef.current, initialVisibleFields);
      dispatch({ type: 'SET_VISIBLE', visibleFields: visible });

      if (cancelled) return;

      // Run field initialization in topological levels (siblings in parallel,
      // dependents wait for parents).
      const levels = computeTopoLevels(graph);
      for (const level of levels) {
        if (cancelled) return;
        await Promise.all(level.map((name) => initializeField(name)));
      }

      if (cancelled) return;
      dispatch({ type: 'SET_INITIALIZED' });
      lastPersistedRef.current = currentValues(stateRef.current);
    };

    runInit().catch((err) => {
      console.warn('[FilterBar] init failed:', err);
    });

    return () => {
      cancelled = true;
      registry.current.abortAll();
    };
  }, []);

  // ---------- Emission effect ----------

  const prevStateRef = useRef<State>(state);
  useEffect(() => {
    const prev = prevStateRef.current;
    const next = state;
    prevStateRef.current = next;
    if (prev === next) return;
    emitIfReady(prev, next, {
      onInit: callbacksRef.current.onInit,
      onChange: callbacksRef.current.onChange,
      onFieldChange: callbacksRef.current.onFieldChange,
      initializedRef,
    });
  }, [state]);

  // ---------- Persistence auto-save effect ----------

  useEffect(() => {
    if (!persistence || !autoSave) return;
    if (!state.initialized) return;
    if (state.closedTxnCount === 0) return;

    const values = currentValues(state);
    const ms = persistence.debounceMs ?? 0;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(() => {
      Promise.resolve(persistence.save(values))
        .then(() => {
          lastPersistedRef.current = values;
          setPersistenceTick((t) => t + 1);
        })
        .catch((err) => console.warn('[FilterBar] persistence.save failed:', err));
    }, ms);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [state.closedTxnCount, state.initialized, persistence, autoSave]);

  // Derived `isDirty` — compares current values to last-saved/loaded snapshot.
  const isDirty = useMemo(() => {
    if (!persistence) return false;
    if (!state.initialized) return false;
    const values = currentValues(state);
    const last = lastPersistedRef.current ?? {};
    return !shallowEqualValues(values, last);
  }, [state, persistence, persistenceTick]);

  // ---------- Controlled-mode prop sync ----------

  useEffect(() => {
    if (!controlledValues) return;
    if (!initializedRef.current) return;
    // Compare against current internal values; if different, sync without
    // opening a transaction (echo silence).
    const internal = currentValues(stateRef.current);
    if (shallowEqualValues(controlledValues as FilterValues, internal)) return;
    // Apply each diff without transaction.
    for (const k of Object.keys(controlledValues as FilterValues)) {
      const v = (controlledValues as FilterValues)[k];
      if (!shallowEqualValue(v, internal[k])) {
        dispatch({ type: 'FIELD_SET_VALUE', name: k, value: v, userTouched: false });
      }
    }
  }, [controlledValues]);

  // ---------- Controller ----------

  const set = useCallback((name: string, value: unknown): void => {
    txnIncrement();
    ensureTxnOpen('user', name);
    dispatch({ type: 'FIELD_SET_VALUE', name, value, userTouched: true });
    runCascadeFrom(name, { source: 'user', trigger: name }).finally(txnDecrement);
  }, []);

  const setMany = useCallback(
    (patch: FilterValues, opts: { cascade?: boolean } = {}): void => {
      const cascade = opts.cascade !== false;
      const source = 'imperative';
      const keys = Object.keys(patch);
      if (keys.length === 0) return;
      ensureTxnOpen(source, keys[0]);
      for (const k of keys) {
        dispatch({ type: 'FIELD_SET_VALUE', name: k, value: patch[k], userTouched: true });
      }
      txnIncrement();
      if (cascade) {
        // Run cascade for each set field's dependents.
        const allDependents = new Set<string>();
        for (const k of keys) {
          for (const d of getDescendants(graph, k)) {
            if (!keys.includes(d)) allDependents.add(d);
          }
        }
        if (allDependents.size > 0) {
          Promise.all(
            Array.from(allDependents).map((dep) => {
              const item = getItem(dep);
              const policy = item?.dependsOn?.find((d) => keys.includes(d.name))?.onParentChange ?? 'reset';
              return cascadeField(dep, policy);
            }),
          ).finally(txnDecrement);
        } else {
          Promise.resolve().finally(txnDecrement);
        }
      } else {
        Promise.resolve().finally(txnDecrement);
      }
    },
    [graph],
  );

  /**
   * Per-field rule selector — given a field config and the operation
   * (`clear` vs `reset`), returns what the field's value should become.
   *
   * Returns `'BASELINE'` to signal "restore baseline" (orchestrator runs
   * resolveDefault), or a literal value (which may be `canonical empty`).
   */
  type OperationTarget = unknown | 'BASELINE';
  const targetFor = (item: FilterItemConfig, op: 'clear' | 'reset'): OperationTarget => {
    if (op === 'reset') return 'BASELINE';
    // op === 'clear'
    return item.clearable === false ? 'BASELINE' : canonicalEmpty(item);
  };

  /**
   * Run a "clear" or "reset" operation against a set of fields in one transaction.
   *
   * Plan Section "Cascade behavior for clear / clearAll / reset / resetAll":
   *   - Each field gets a target per `targetFor(item, op)`.
   *   - Targets are assigned in dep order (parents first).
   *   - Parent value changes open the standard cascade to dependents, but the
   *     dependent's operation-target wins over `onParentChange` policy
   *     (the operation has the final say on what the value becomes).
   *   - Pills hit `LOADING_OPTIONS` as their options reload; siblings stay interactive.
   *   - Single onChange after the whole cascade settles.
   */
  const runOperation = (op: 'clear' | 'reset', fieldNames: string[], trigger?: string): void => {
    if (fieldNames.length === 0) return;
    txnIncrement();
    ensureTxnOpen('imperative', trigger);

    // 1) Compute per-field targets up-front. Fields touched by this operation
    //    are tagged so cascade picks the operation target over onParentChange.
    const operationTargets = new Map<string, OperationTarget>();
    for (const n of fieldNames) {
      const item = getItem(n);
      if (!item) continue;
      operationTargets.set(n, targetFor(item, op));
    }

    (async () => {
      // 2) Apply targets in topological order so parents settle before dependents.
      const topo = stateRef.current.itemsOrder.filter((n) => operationTargets.has(n));
      // Group by level (independent root fields can run in parallel).
      const orderedLevels = computeLevels(graph, topo);

      for (const level of orderedLevels) {
        await Promise.all(level.map(async (name) => {
          const item = getItem(name)!;
          const target = operationTargets.get(name);

          // Sync assignment for literal targets (canonical empty).
          if (target !== 'BASELINE') {
            dispatch({ type: 'FIELD_SET_VALUE', name, value: target, userTouched: false });
            return;
          }

          // Baseline: defaultValue if set (sync), else rerun getDefaultValue
          // against current options (async — may require options reload first
          // if parent changed but that happens in the cascade step below).
          const currentOptions = stateRef.current.fields[name]?.options ?? [];
          const baseline = await resolveDefault(item, currentOptions);
          if ((baseline === null || baseline === undefined) && item.clearable === false) {
            dispatch({
              type: 'FIELD_ERROR',
              name,
              error: `No valid default available for non-clearable field "${name}".`,
            });
            return;
          }
          dispatch({
            type: 'FIELD_SET_VALUE',
            name,
            value: baseline ?? canonicalEmpty(item),
            userTouched: false,
          });
        }));
      }

      // 3) Cascade to dependents. operationTargets ensures children touched by
      //    this operation use their declared target, NOT onParentChange.
      const allDependents = new Set<string>();
      for (const n of fieldNames) {
        for (const d of getDescendants(graph, n)) allDependents.add(d);
      }
      // Strip out fields directly in this operation — they were handled above.
      for (const n of fieldNames) allDependents.delete(n);

      await Promise.all(
        Array.from(allDependents).map(async (dep) => {
          const depItem = getItem(dep);
          if (!depItem) return;
          // Reload options against the new parent values.
          const opts = await loadOptionsForField(dep, { useCache: true });
          if (!opts) return;
          // If this dependent ALSO has an operation target (clearAll/resetAll
          // touches it directly), apply that target over its new options.
          if (operationTargets.has(dep)) {
            const tgt = operationTargets.get(dep);
            if (tgt !== 'BASELINE') {
              dispatch({ type: 'FIELD_SET_VALUE', name: dep, value: tgt, userTouched: false });
            } else {
              const baseline = await resolveDefault(depItem, opts);
              if ((baseline === null || baseline === undefined) && depItem.clearable === false) {
                dispatch({
                  type: 'FIELD_ERROR',
                  name: dep,
                  error: `No valid default available for non-clearable field "${dep}".`,
                });
                return;
              }
              dispatch({
                type: 'FIELD_SET_VALUE',
                name: dep,
                value: baseline ?? canonicalEmpty(depItem),
                userTouched: false,
              });
            }
            return;
          }
          // Otherwise it's a normal cascade — apply the dependent's onParentChange.
          const parentEntry = depItem.dependsOn?.find((d) => fieldNames.includes(d.name));
          const policy = parentEntry?.onParentChange ?? 'reset';
          await handleStaleValueAndDefault(dep, opts, policy);
        }),
      );
    })().finally(txnDecrement);
  };

  const clear = useCallback((name: string): void => {
    if (!getItem(name)) return;
    runOperation('clear', [name], name);
  }, []);

  const clearAll = useCallback((): void => {
    runOperation('clear', stateRef.current.itemsOrder, undefined);
  }, [graph]);

  const reset = useCallback((name: string): void => {
    if (!getItem(name)) return;
    runOperation('reset', [name], name);
  }, []);

  const resetAll = useCallback((): void => {
    runOperation('reset', stateRef.current.itemsOrder, undefined);
  }, [graph]);

  const reload = useCallback(
    (name?: string): void => {
      ensureTxnOpen('imperative', name);
      txnIncrement();
      const names = name ? [name] : stateRef.current.itemsOrder;
      Promise.all(
        names.map(async (n) => {
          cache.current.invalidate(n);
          const opts = await loadOptionsForField(n, { useCache: false });
          if (opts) await handleStaleValueAndDefault(n, opts);
        }),
      ).finally(txnDecrement);
    },
    [],
  );

  const addField = useCallback((name: string): void => {
    const item = getItem(name);
    if (!item) return;
    ensureTxnOpen('imperative', name);
    dispatch({ type: 'ADD_FIELD', name });
    // Re-init this field — full default → getDefaultValue chain.
    txnIncrement();
    (async () => {
      if (item.defaultValue !== undefined) {
        dispatch({ type: 'FIELD_SET_VALUE', name, value: item.defaultValue, userTouched: false });
      } else {
        dispatch({ type: 'FIELD_SET_VALUE', name, value: canonicalEmpty(item), userTouched: false });
      }
      const opts = await loadOptionsForField(name);
      if (opts) await handleStaleValueAndDefault(name, opts);
    })().finally(txnDecrement);
  }, []);

  const removeField = useCallback((name: string): void => {
    txnIncrement();
    ensureTxnOpen('imperative', name);
    dispatch({ type: 'REMOVE_FIELD', name });
    runCascadeFrom(name, { source: 'imperative', trigger: name }).finally(txnDecrement);
  }, []);

  const getOptions = useCallback(
    (name: string): SelectOptionType[] => stateRef.current.fields[name]?.options ?? [],
    [],
  );

  const findOption = useCallback((name: string, value: unknown): SelectOptionType | undefined => {
    const opts = stateRef.current.fields[name]?.options ?? [];
    return findOptionInTree(opts, value);
  }, []);

  const getSelectedOptions = useCallback(
    (name: string): SelectOptionType[] => {
      const field = stateRef.current.fields[name];
      if (!field) return [];
      const opts = field.options;
      const val = field.value;
      if (val === null || val === undefined || val === '') return [];
      if (Array.isArray(val)) {
        return val
          .map((v) => findOptionInTree(opts, v))
          .filter((o): o is SelectOptionType => o !== undefined);
      }
      const found = findOptionInTree(opts, val);
      return found ? [found] : [];
    },
    [],
  );

  const isLoading = useCallback(
    (name: string): boolean => {
      const field = stateRef.current.fields[name];
      if (!field) return false;
      return field.phase === 'LOADING_OPTIONS' || field.phase === 'LOADING_DEFAULT';
    },
    [],
  );

  const isLoadingAny = useMemo(
    () => state.itemsOrder.some((n) => {
      const f = state.fields[n];
      return f && (f.phase === 'LOADING_OPTIONS' || f.phase === 'LOADING_DEFAULT');
    }),
    [state],
  );

  const getPhase = useCallback(
    (name: string): FilterFieldPhase => stateRef.current.fields[name]?.phase ?? 'IDLE',
    [],
  );

  const getError = useCallback(
    (name: string): string | null => stateRef.current.fields[name]?.error ?? null,
    [],
  );

  // Persistence controller
  const persistenceController = useMemo(() => {
    if (!persistence) return undefined;
    return {
      save: async () => {
        const values = currentValues(stateRef.current);
        try {
          await Promise.resolve(persistence.save(values));
          lastPersistedRef.current = values;
          setPersistenceTick((t) => t + 1);
        } catch (err) {
          console.warn('[FilterBar] persistence.save failed:', err);
        }
      },
      load: async () => {
        try {
          const loaded = await Promise.resolve(persistence.load());
          if (loaded) {
            lastPersistedRef.current = loaded;
            ensureTxnOpen('imperative', undefined);
            for (const k of Object.keys(loaded)) {
              dispatch({ type: 'FIELD_SET_VALUE', name: k, value: loaded[k], userTouched: false });
            }
            // Close after sync seed
            queueMicrotask(() => {
              if (pendingTxnRef.current === 0 && stateRef.current.transactionOpen) {
                dispatch({ type: 'CASCADE_CLOSE' });
              }
            });
            setPersistenceTick((t) => t + 1);
          }
        } catch (err) {
          console.warn('[FilterBar] persistence.load failed:', err);
        }
      },
      clear: async () => {
        try {
          if (persistence.clear) await Promise.resolve(persistence.clear());
          lastPersistedRef.current = null;
          // Wipe in-memory state too — apply the per-field clear rule across
          // every field, then mark the bar undirty.
          runOperation('clear', stateRef.current.itemsOrder, undefined);
          setPersistenceTick((t) => t + 1);
        } catch (err) {
          console.warn('[FilterBar] persistence.clear failed:', err);
        }
      },
      isDirty,
    };
  }, [persistence, isDirty]);

  const controller: FilterBarController<T> = useMemo(
    () => ({
      values: currentValues(state) as T,
      isReady: state.initialized,
      isLoading: isLoading as FilterBarController<T>['isLoading'],
      isLoadingAny,
      visibleFields: state.visibleFields,
      items: state.itemsOrder.map((n) => state.itemsByName[n]),
      getOptions: getOptions as FilterBarController<T>['getOptions'],
      getSelectedOptions: getSelectedOptions as FilterBarController<T>['getSelectedOptions'],
      findOption: findOption as FilterBarController<T>['findOption'],
      set: set as FilterBarController<T>['set'],
      setMany: setMany as FilterBarController<T>['setMany'],
      clear: clear as FilterBarController<T>['clear'],
      clearAll,
      reload: reload as FilterBarController<T>['reload'],
      reset: reset as FilterBarController<T>['reset'],
      resetAll,
      addField: addField as FilterBarController<T>['addField'],
      removeField: removeField as FilterBarController<T>['removeField'],
      getPhase: getPhase as FilterBarController<T>['getPhase'],
      getError: getError as FilterBarController<T>['getError'],
      persistence: persistenceController,
    }),
    [
      state,
      isLoading,
      isLoadingAny,
      getOptions,
      getSelectedOptions,
      findOption,
      set,
      setMany,
      clear,
      clearAll,
      reload,
      reset,
      resetAll,
      addField,
      removeField,
      getPhase,
      getError,
      persistenceController,
    ],
  );

  return controller;
}

// ---------- Internal helpers ----------

function flattenOptionValues(options: SelectOptionType[]): Set<string | number> {
  const out = new Set<string | number>();
  const walk = (nodes: SelectOptionType[]) => {
    for (const n of nodes) {
      if (n.value !== undefined) out.add(n.value);
      if (n.options) walk(n.options);
      if (n.children) walk(n.children);
    }
  };
  walk(options);
  return out;
}

function findOptionInTree(options: SelectOptionType[], value: unknown): SelectOptionType | undefined {
  for (const opt of options) {
    if (opt.value === value) return opt;
    if (opt.options) {
      const sub = findOptionInTree(opt.options, value);
      if (sub) return sub;
    }
    if (opt.children) {
      const sub = findOptionInTree(opt.children, value);
      if (sub) return sub;
    }
  }
  return undefined;
}

function computeTopoLevels(graph: DependencyGraph): string[][] {
  return computeLevels(graph, graph.order);
}

/**
 * Build topological levels (parents first, siblings parallel) over a SUBSET of
 * the graph. Edges into nodes outside `subset` are ignored — the subset is
 * scheduled as if those external parents had already settled.
 */
function computeLevels(graph: DependencyGraph, subset: string[]): string[][] {
  const subsetSet = new Set(subset);
  const inDegree = new Map<string, number>();
  for (const name of subset) {
    const parents = graph.parentsOf.get(name) ?? new Set<string>();
    let degree = 0;
    for (const p of parents) if (subsetSet.has(p)) degree += 1;
    inDegree.set(name, degree);
  }
  const remaining = new Set(subset);
  const levels: string[][] = [];
  while (remaining.size > 0) {
    const current: string[] = [];
    for (const n of remaining) {
      if ((inDegree.get(n) ?? 0) === 0) current.push(n);
    }
    if (current.length === 0) break;
    levels.push(current);
    for (const n of current) {
      remaining.delete(n);
      for (const dep of graph.dependentsOf.get(n) ?? []) {
        if (!subsetSet.has(dep)) continue;
        inDegree.set(dep, (inDegree.get(dep) ?? 0) - 1);
      }
    }
  }
  return levels;
}

function computeInitialVisibility(
  items: FilterItemConfig[],
  initialVisibleFields: string[] | undefined,
): string[] {
  if (initialVisibleFields) {
    // pinned fields always visible regardless
    const out = new Set(initialVisibleFields);
    for (const item of items) {
      if (item.pinned) out.add(item.name);
    }
    return Array.from(out);
  }
  // Default: show all fields. The + menu can remove non-pinned ones.
  return items.map((i) => i.name);
}

// Public re-export of helper exported by hook
export { shallowEqualValue, shallowEqualValues } from '../state/init-sequencer';
